import { computed } from 'vue';
import { toValue } from '@vueuse/core';
import { useStore } from 'vuex';

export default function useInterviewLifecycle(
  interview,
  { recruiterId, applicantEmail, applicationInterviews } = {}
) {
  const store = useStore();

  const currentUser = computed(() => store.state.auth?.user);

  const cancellingReasons = computed(
    () => toValue(interview)?.cancelling_reasons ?? []
  );

  const isMarkedAsNoShow = computed(() =>
    Boolean(toValue(interview)?.marked_as_no_show_at)
  );

  const isKaratInterview = computed(() => toValue(interview)?.type === 'KARAT');

  const isCancelled = computed(
    () =>
      Boolean(cancellingReasons.value.length) ||
      isMarkedAsNoShow.value ||
      toValue(interview)?.calendarEvent?.status === 'cancelled' ||
      toValue(interview)?.karatInterview?.state === 'cancelled'
  );

  const cancelledBy = computed(() =>
    isMarkedAsNoShow.value ? null : (toValue(interview)?.cancelled_by ?? null)
  );

  const cancellationLabel = computed(() => 'Cancelled');

  const noShowLabel = computed(() =>
    isMarkedAsNoShow.value ? '(No Show)' : ''
  );

  const cancellationBadgeLabel = computed(() =>
    [cancellationLabel.value, noShowLabel.value].filter(Boolean).join(' ')
  );

  const cancellationReasonsLabel = computed(() => {
    if (isMarkedAsNoShow.value) return '';

    return cancellingReasons.value
      .map(({ title, details }) => (details ? `${title}: ${details}` : title))
      .join(', ');
  });

  const isRescheduled = computed(() =>
    Boolean(toValue(interview)?.rescheduling_reason)
  );

  const hasActiveInterviewOfSameType = computed(() => {
    const interviews = toValue(applicationInterviews);

    if (!Array.isArray(interviews)) return true;

    const { id, type } = toValue(interview) ?? {};

    return interviews.some(
      (candidate) =>
        candidate?.id !== id &&
        candidate?.type === type &&
        Boolean(candidate?.active)
    );
  });

  const canReschedule = computed(() => {
    const { active, calendarEvent } = toValue(interview) ?? {};

    if (!calendarEvent || isRescheduled.value) return false;

    return isCancelled.value
      ? !hasActiveInterviewOfSameType.value
      : Boolean(active);
  });

  const hasApplicantDeclined = computed(() => {
    const interviewValue = toValue(interview);
    const email = (
      toValue(applicantEmail) ??
      interviewValue?.application?.email ??
      ''
    ).toLowerCase();

    if (!email) return false;

    return (interviewValue?.calendarEvent?.attendees ?? []).some(
      (attendee) =>
        attendee.response === 'declined' &&
        attendee.email?.toLowerCase() === email
    );
  });

  const applicationRecruiterId = computed(() => {
    const application = toValue(interview)?.application;

    return (
      toValue(recruiterId) ??
      application?.recruiter_id ??
      application?.assignees?.recruiter?.id
    );
  });

  const canCancel = computed(() => {
    const { id: userId } = currentUser.value ?? {};
    const { interviewer, calendarEvent, active, karatInterview } =
      toValue(interview) ?? {};

    if (isKaratInterview.value) {
      return Boolean(karatInterview?.eligible_to_cancel) && !isCancelled.value;
    }

    if (!userId) return false;

    return (
      (userId === interviewer?.id || userId === applicationRecruiterId.value) &&
      Boolean(active) &&
      Boolean(calendarEvent?.isUpcoming) &&
      !isCancelled.value
    );
  });

  return {
    isKaratInterview,
    cancellingReasons,
    isMarkedAsNoShow,
    isCancelled,
    cancelledBy,
    cancellationLabel,
    noShowLabel,
    cancellationBadgeLabel,
    cancellationReasonsLabel,
    isRescheduled,
    hasApplicantDeclined,
    canCancel,
    canReschedule,
    hasActiveInterviewOfSameType,
  };
}
